import { get } from 'lodash';
import { List, Map as ImmutableMap } from 'immutable';
import Leg from '../records/order/Leg';
import OrderTrip from '../records/order/Trip';
import { SplitTicketTypes } from '../constants/AppConstants';
import { getDuration } from './date';
import t from '../lib/I18n';

/**
 * Sorts an array of fare objects by their first split section.
 * @param {Array<Object>} fares - Array of fare objects, each with a splitSections property.
 * @returns {Array<Object>} Sorted array of fares by split section.
 */
export const sortFaresBySplitSection = (fares) => {
    return [...fares].sort((a, b) => {
        const aSection = get(a, 'splitSections[0]', 0);
        const bSection = get(b, 'splitSections[0]', 0);
        return aSection - bSection;
    });
};

// A ticket combining an adult and a child on one row can't be partially amended - there's only
// one ticket to supersede for two people. Shared by AmendRequest#isPassengerDisabledForAmend and
// AmendSplitPart's row-level UI hint, so the two can't silently disagree on what counts as
// combined.
export const isCombinedFareTicket = (ticket) => ticket.adults > 0 && ticket.children > 0;

// Backend split sections carry no passenger identity (no passenger rows; seats are per-leg),
// so there is nothing to key a cross-section match on beyond what's visible on the ticket
// itself. Grouping by this signature keeps same-type tickets (e.g. two adults) together at the
// same relative position on every section, and correctly separates different passenger types
// (e.g. adult vs child) that would otherwise be picked up by raw array position. Leads with
// children (not adults) so an adult ticket's key sorts before a child's, matching the
// adults-first convention non-split fares already use (adultsFirst in helpers/TicketSorters.js).
const passengerSignature = (ticket) =>
    `${ticket.children}_${ticket.adults}_${ticket.railcard?.code ?? ''}`;

// Callers (groupFaresByPassenger, AmendSplitPart's getFareTicketPairs) re-derive the same fare's
// ordered tickets once per passengerIndex in a loop - cache per fare instance so a section's
// tickets are only ever sorted once, same pattern as AmendedTicket's #oldFareCache.
const orderedTicketsCache = new WeakMap();

/**
 * Orders a fare's tickets deterministically by passenger signature (adults/children/railcard),
 * with ticket id as a tiebreaker. Two tickets with an identical signature are interchangeable -
 * the tiebreaker only needs to be stable, not meaningful, so any consistent ordering is valid.
 * @param {Object} fare - A fare object with a tickets List.
 * @returns {List} The fare's tickets, canonically ordered.
 */
export const getOrderedPassengerTickets = (fare) => {
    if (!orderedTicketsCache.has(fare)) {
        const ordered = fare.tickets.sortBy(
            (ticket) => `${passengerSignature(ticket)}_${ticket.id}`,
        );
        orderedTicketsCache.set(fare, ordered);
    }
    return orderedTicketsCache.get(fare);
};

/**
 * Pairs each fare with the ticket belonging to one passenger, dropping legs that don't have a
 * ticket for that passenger. Passenger identity is the ticket's index position within its fare's
 * canonically-ordered tickets (see getOrderedPassengerTickets) — legs of one split list the same
 * passenger at the same index once ordered this way.
 * @param {Array<Object>} fares - Array of fare objects, each with a tickets List.
 * @param {number} passengerIndex - Index position identifying the passenger.
 * @returns {Array<{fare: Object, ticket: Object}>} One {fare, ticket} pair per leg that has this passenger.
 */
export const getPassengerFareTicketPairs = (fares, passengerIndex) => Array.from(fares)
    .map(fare => ({ fare, ticket: getOrderedPassengerTickets(fare).get(passengerIndex) }))
    .filter(({ ticket }) => ticket);

/**
 * Splits a flat list of leg fares (each potentially carrying multiple passengers) into one
 * group of single-ticket fares per passenger, ordered by split section. Passenger identity is
 * the ticket's index position within its fare's canonically-ordered tickets (see
 * getPassengerFareTicketPairs/getOrderedPassengerTickets), matching the convention used by
 * deriveSplitDisplayValues/AmendSplitPart.
 *
 * Fares are first partitioned by their own splitId (falling back to fareId for a non-split
 * fare) before pairing by passenger index. Trip.js's asymmetric regular+split merge
 * (mergeReturnSingles, for a trip group where only one direction is still split) can hand this
 * function fares from two unrelated splits/directions in one flat list — without partitioning
 * first, passenger-index pairing would wrongly combine, say, a regular non-split outward fare
 * with an unrelated split return leg into one row.
 * @param {Array<Object>} fares - Array of fare objects, each with a tickets List.
 * @returns {Array<Array<Object>>} One array of single-ticket fares per passenger.
 */
export const groupFaresByPassenger = (fares) => {
    const keyOrder = [];
    const partitions = fares.reduce(
        (byKey, fare) => {
            const key = fare.splitId || `fare_${fare.fareId}`;
            if (!byKey.has(key)) {
                keyOrder.push(key);
            }
            return byKey.update(key, new List(), partitionFares => partitionFares.push(fare));
        },
        new ImmutableMap(),
    );

    return keyOrder.map(key => partitions.get(key)).flatMap(partitionFares => {
        const maxPassengers = partitionFares.reduce((max, fare) => Math.max(max, fare.tickets.size), 0);
        return Array.from({ length: maxPassengers }, (_, passengerIndex) => passengerIndex)
            .map(passengerIndex => getPassengerFareTicketPairs(partitionFares, passengerIndex)
                .map(({ fare, ticket }) => fare.set('tickets', new List([ticket]))))
            .filter(group => group.length)
            .map(sortFaresBySplitSection);
    });
};

/**
 * Flattens and sorts fares from an array of items, each with getJourneyFaresSortedByFareId().
 * @param {Array<Object>} items - Array of Trips, each with getJourneyFaresSortedByFareId method.
 * @returns {Array<Object>} Sorted array of fares from all items.
 */
export const getSortedFaresFromItems = (items) => {
    if (!items || !items.length) {
        return [];
    }
    const rawFares = items.flatMap((item) => {
        if (item) {
            return item.getJourneyFaresSortedByFareId();
        }
        return [];
    });
    return sortFaresBySplitSection(rawFares);
};

/**
 * Groups journey legs by split status. Consecutive split legs are grouped together; non-split legs are separate groups.
 * @param {Array<Object>} legs - Array of leg objects, each may have a 'split' property.
 * @returns {Array<Array<Object>>} Array of grouped leg arrays.
 */
export const groupLegsBySplits = (legs) => {
    if (!legs || !legs.length) {
        return [];
    }

    const groupedLegs = legs.reduce((acc, leg) => {
        if (leg.split) {
            // if last group exists and is also a "split" group → push into it
            if (get(acc, [acc.length - 1, 0, 'split'])) {
                acc[acc.length - 1].push(leg);
            } else {
                // otherwise start new "split" group
                acc.push([leg]);
            }
        } else {
            // non-split items always have their own group
            acc.push([leg]);
        }

        return acc;
    }, []);

    const uniqueGroups = new Map(
        groupedLegs.map((group) => {
            return [
                group
                    .map((leg) => leg instanceof Leg ?
                        `${get(leg, 'origin.name', '')}-${get(leg, 'destination.name', '')}` :
                        `${get(leg, 'origin[0].station.name', '')}-${get(leg, 'destination[0].station.name', '')}`
                    )
                    .join(),
                group,
            ];
        })
    );

    return Array.from(uniqueGroups.values());
};

/**
 * Extracts trips of a given type ('regular' or 'split') from groupedTrips.
 * @param {Array<Object>} groupedTrips - Array of trip group objects.
 * @param {'regular'|'split'} type - The type of trips to extract.
 * * @returns {Array<OrderTrip>} Array of trips of the specified type.
 */
export const extractTripsByType = (groupedTrips, type) => {
    return groupedTrips
        .filter(trip => trip[type])
        .map(trip => trip[type]);
};

// items are trips array
export const getGroupedTrips = ({ items }) => {
    const { outwardSingleItems, returnSingleItems, returnItems } = (() => {
        if (!items || !items.length) {
            return {};
        }

        // Find the cross-reference IDs that distinguish outward from return split groups.
        // outwardSplitReferenceId on a trip means it belongs to the return group (linked back to outward).
        // returnSplitReferenceId on a trip means it belongs to the outward group (linked forward to return).
        const outwardSingleSplitReferenceId = (items.find(item => item?.outwardSplitReferenceId) || {}).outwardSplitReferenceId || null;
        const returnSingleSplitReferenceId = (items.find(item => item?.returnSplitReferenceId) || {}).returnSplitReferenceId || null;

        return items.reduce((acc, item) => {
            if (item?.isPartOfSplitAsReturn()) {
                return { ...acc, returnItems: [...(acc.returnItems || []), item] };
            }

            if (
                item?.isPartOfSplitAsSingleOutward() &&
                item.getFareSplitId() !== returnSingleSplitReferenceId
            ) {
                return { ...acc, outwardSingleItems: [...(acc.outwardSingleItems || []), item] };
            }

            if (
                item?.isPartOfSplitAsSingleReturn() &&
                item.getFareSplitId() !== outwardSingleSplitReferenceId
            ) {
                return { ...acc, returnSingleItems: [...(acc.returnSingleItems || []), item] };
            }

            return acc;
        }, {});
    })();

    return { outwardSingleItems, returnSingleItems, returnItems };
};

export const getSplitFares = ({ items, isReturn }) => {
    const { outwardSingleItems, returnSingleItems, returnItems } = getGroupedTrips({ items });
    const outwardSingleSplitFares = getSortedFaresFromItems(outwardSingleItems);
    const returnSingleSplitFares = getSortedFaresFromItems(returnSingleItems);
    const returnSplitFares = getSortedFaresFromItems(returnItems);

    if (returnSplitFares && returnSplitFares.length) {
        return returnSplitFares;
    }

    if (isReturn) {
        // A return-only amend selection carries only return legs. With no outward
        // counterpart present to compare cross-reference ids against, getGroupedTrips
        // can't tell those legs apart from outward ones and defaults them into
        // outwardSingleItems. If nothing landed in either return bucket, treat the
        // (mis-bucketed) outward fares as the return ones rather than returning none.
        return returnSingleSplitFares.length ? returnSingleSplitFares : outwardSingleSplitFares;
    }

    return outwardSingleSplitFares;
};

export const getJourneyFromSplitTrips = ({ items, isReturn }) => {
    const { outwardSingleItems, returnSingleItems, returnItems } = getGroupedTrips({ items });

    const outwardFares = getSortedFaresFromItems(outwardSingleItems);
    const returnFares = getSortedFaresFromItems(returnItems && returnItems.length ? returnItems : returnSingleItems);

    // See getSplitFares above: a return-only selection of plain (unreferenced) split
    // legs all lands in outwardSingleItems, leaving returnFares empty.
    const usesOutwardAsReturnFallback = isReturn && !returnFares.length && outwardFares.length;
    const effectiveOutwardFares = usesOutwardAsReturnFallback ? [] : outwardFares;
    const effectiveReturnFares = usesOutwardAsReturnFallback ? outwardFares : returnFares;

    const firstEffectiveOutwardFare = effectiveOutwardFares[0];
    const firstEffectiveReturnFare = effectiveReturnFares[0];

    const outwardJourney = firstEffectiveOutwardFare && firstEffectiveOutwardFare.outwardJourney;
    const returnJourney = firstEffectiveReturnFare
        ? firstEffectiveReturnFare.returnJourney || firstEffectiveReturnFare.outwardJourney
        : null;

    return { outwardJourney, returnJourney };
};

/**
 * Merges one direction's journeys from all split parts into a single
 * whole-journey record. Split-boundary legs (same physical train crossing a
 * split point, marked split: true) are collapsed into one through-leg keeping
 * the first leg's train/departure/origin and the last leg's destination/arrival;
 * legs on different trains stay separate.
 * @param {Array<Journey>} journeys - one direction's journeys in travel (split-section) order.
 * @returns {Journey|null} the first journey with the merged, departure-sorted legs.
 */
export const mergeSplitJourneysIntoOne = (journeys) => {
    if (!journeys || !journeys.length) {
        return null;
    }

    const allLegs = journeys.flatMap((journey) => Array.from(get(journey, 'legs', [])));

    const mergedLegs = groupLegsBySplits(allLegs).map((group) => {
        if (group.length === 1) {
            return group[0];
        }
        const firstLeg = group[0];
        const lastLeg = group[group.length - 1];
        return firstLeg.merge({ destination: lastLeg.destination, arrival: lastLeg.arrival });
    });

    const sortedLegs = [...mergedLegs].sort((a, b) => {
        const aDeparture = a.departure ? a.departure.valueOf() : Infinity;
        const bDeparture = b.departure ? b.departure.valueOf() : Infinity;
        return aDeparture - bDeparture;
    });

    return journeys[0].set('legs', new List(sortedLegs));
};

/**
 * Derives one merged whole-journey record per direction from a split group's
 * trips, for flows that need the full journey rather than per-part segments
 * (e.g. disruption alert creation — one alert per direction).
 * @param {Array<OrderTrip>|List<OrderTrip>} trips - one split group's trips, any order.
 * @returns {{outwardJourney: Journey|null, returnJourney: Journey|null}}
 */
export const getMergedSplitJourneys = (trips) => {
    const items = Array.from(trips || []);
    const { outwardSingleItems, returnSingleItems, returnItems } = getGroupedTrips({ items });

    const outwardSingleFares = getSortedFaresFromItems(outwardSingleItems);
    const returnSingleFares = getSortedFaresFromItems(returnSingleItems);
    const returnFares = getSortedFaresFromItems(returnItems);

    const outwardJourneys = returnFares.length
        ? returnFares.map((fare) => fare.outwardJourney)
        : outwardSingleFares.map((fare) => fare.outwardJourney);

    // the inbound direction travels the split sections in reverse order;
    // single-return fares store their journey under outwardJourney
    const returnJourneys = returnFares.length
        ? [...returnFares].reverse().map((fare) => fare.returnJourney)
        : [...returnSingleFares].reverse().map((fare) => fare.outwardJourney);

    return {
        outwardJourney: mergeSplitJourneysIntoOne(outwardJourneys.filter(Boolean)),
        returnJourney: mergeSplitJourneysIntoOne(returnJourneys.filter(Boolean)),
    };
};

/**
 * Partitions trips sharing one splitSalt into one sub-array per distinct
 * outward/return split-id pairing. Amending only some passengers' tickets on a
 * split journey reuses the original splitSalt for the new trips, so a salt can
 * end up spanning two unrelated pairings (e.g. the untouched passenger's old
 * return pair and the amended passenger's new outward+return pair) — grouping
 * by splitSalt alone would merge them into one (wrong) display group.
 * Each pairing's legs carry a consistent (own splitId, cross-reference id)
 * pair, so the sorted pair is a stable key regardless of which side of the
 * pairing an item is on or whether the other side is even present.
 * @param {Array<OrderTrip>} items - trips sharing one splitSalt.
 * @returns {Array<Array<OrderTrip>>} one array of trips per distinct pairing.
 */
export const groupTripsByPairing = (items) => {
    if (!items) {
        return [];
    }
    const list = Array.from(items); // items may be an Immutable List (uses .size, not .length) or a plain array
    if (!list.length) {
        return [];
    }

    const groups = new Map();
    list.forEach((item, index) => {
        const ownId = item.getFareSplitId ? item.getFareSplitId() : null;
        const crossRefId = item.outwardSplitReferenceId || item.returnSplitReferenceId || null;
        const key = ownId || crossRefId
            ? [ownId, crossRefId].filter(Boolean).sort().join('|')
            : `unclassified-${index}`; // never drop items with no id of their own

        groups.set(key, [...(groups.get(key) || []), item]);
    });

    return Array.from(groups.values());
};

export const buildSplitGroup = (trips, splitSalt) => {
    const splitGroup = trips.filter((item) => {
        const fareSplitId = item.getFareSplitId ? item.getFareSplitId() : null;
        return !!fareSplitId && item.splitSalt === splitSalt;
    });

    return groupTripsByPairing(splitGroup).map((pairingItems) => {
        const group = pairingItems.reduce((acc, item) => ({
            ...acc,
            splitSalt,
            splitIds: [...new Set([...(acc.splitIds || []), item.getSplitId()]).values()],
            items: [...(acc.items || []), item],
            itemIds: [
                ...(acc.itemIds || []),
                item instanceof OrderTrip ? item.getTripId() : item.id,
            ],
        }), {});
        return { ...group, groupKey: [...group.splitIds].sort().join('|') };
    });
};

export const mergeReturnSingles = (wrappers) => {
    // A regular trip's wrapper can appear before or after the split group it
    // cross-references (a regular outward single is created first and so is
    // listed before its split-return legs) — resolve every reference against
    // the whole array up front rather than an in-progress accumulator, so the
    // merge doesn't depend on wrapper order.
    const splitWrapperByRefId = new Map();
    wrappers.forEach((wrapper) => {
        if (wrapper.split) {
            wrapper.split.splitIds.forEach((id) => splitWrapperByRefId.set(id, wrapper));
        }
    });

    const extraItemsBySplitWrapper = new Map();
    const mergedRegularWrappers = new Set();
    wrappers.forEach((wrapper) => {
        const refId = wrapper.regular && (wrapper.regular.outwardSplitReferenceId || wrapper.regular.returnSplitReferenceId);
        const targetSplitWrapper = refId ? splitWrapperByRefId.get(refId) : undefined;
        if (targetSplitWrapper) {
            mergedRegularWrappers.add(wrapper);
            const extras = extraItemsBySplitWrapper.get(targetSplitWrapper) || [];
            extras.push(wrapper.regular);
            extraItemsBySplitWrapper.set(targetSplitWrapper, extras);
        }
    });

    return wrappers
        .filter((wrapper) => !mergedRegularWrappers.has(wrapper))
        .map((wrapper) => {
            const extras = wrapper.split && extraItemsBySplitWrapper.get(wrapper);
            if (!extras) {
                return wrapper;
            }
            return { split: { ...wrapper.split, items: [...wrapper.split.items, ...extras] } };
        });
};

/**
 * For a genuine split-fare trip (its own splitId, no cross-reference id of its
 * own), determines whether it belongs to the return direction of an
 * asymmetric split pairing by finding the regular single sibling (if any) in
 * orderTrips that cross-references this trip's split id — mirroring
 * isPartOfSplitAsRegularReturnSingle/isPartOfSplitAsRegularOutwardSingle,
 * which only work when the trip itself carries the cross-reference id.
 * @param {OrderTrip} trip - a genuine split-leg trip to classify.
 * @param {Array<OrderTrip>|List<OrderTrip>} orderTrips - the full order's trips.
 * @returns {boolean} whether this split leg's group is the return direction.
 */
export const isSplitLegReturnDirection = (trip, orderTrips) => {
    const splitId = trip?.getFareSplitId?.();
    if (!splitId || !orderTrips) {
        return false;
    }
    const regularSibling = orderTrips.find(
        (sibling) => sibling && !sibling.getFareSplitId?.() &&
            (sibling.outwardSplitReferenceId === splitId || sibling.returnSplitReferenceId === splitId)
    );
    return !!regularSibling && regularSibling.returnSplitReferenceId === splitId;
};

/**
 * Orders a split group's trips for display: outward legs before return legs,
 * each direction sorted by splitSections. Trip IDs are assigned in creation order,
 * which can interleave outward and return legs, so raw array order is not reliable.
 * @param {Array<OrderTrip>} items - trips belonging to a single split group.
 * @returns {Array<OrderTrip>} trips ordered outward-then-return, by split section.
 */
export const sortSplitGroupTripsForDisplay = (items) => {
    if (!items || !items.length) {
        return [];
    }

    const isClassifiable = (item) => typeof item?.isPartOfSplitAsReturn === 'function';
    if (!items.every(isClassifiable)) {
        return items;
    }

    const { outwardSingleItems = [], returnSingleItems = [], returnItems = [] } = getGroupedTrips({ items });

    const sectionOf = (trip) => get(trip.getJourneyFaresSortedByFareId(), '[0].splitSections[0]', 0);
    const bySection = (a, b) => sectionOf(a) - sectionOf(b);

    const ordered = [
        ...[...outwardSingleItems].sort(bySection),
        ...[...returnItems, ...returnSingleItems].sort(bySection),
    ];

    // trips getGroupedTrips didn't classify (shouldn't happen for real split data) are appended, never dropped
    const classified = new Set(ordered);
    return [...ordered, ...items.filter((item) => !classified.has(item))];
};

/**
 * Finds the earliest departure time among a list of trips, used to order
 * amended trip groups by when they travel. Trips with no departure time
 * (e.g. not yet re-serviced) sort last.
 * @param {Array<Object>} trips - trips with a getDepartureTime() method.
 * @returns {number} the earliest departure timestamp, or Infinity if none have one.
 */
export const earliestDepartureTime = (trips) => trips.reduce((earliest, trip) => {
    // OrderTrip.getDepartureTime() dereferences the outward journey, which is null on fare-less trips
    const hasJourney = !trip?.getOutwardJourney || !!trip.getOutwardJourney();
    const departure = hasJourney ? trip?.getDepartureTime?.() : null;
    return Math.min(earliest, departure ? departure.valueOf() : Infinity);
}, Infinity);

/**
 * Derives every display value a split-journey view needs (origin/destination,
 * dates, legs, ticket type, pricing, etc.) from a group of split trips.
 * A pure function rather than a hook: none of these values are ever updated
 * after being computed, so there's nothing here that needs React's render cycle.
 * @param {Array<OrderTrip>} trips - trips belonging to one split group.
 * @param {boolean} [isReturnPart] - whether this is the return part of a round trip;
 *   null means "decide automatically" (return part if the outbound has already expired).
 * @param {boolean} [enableAdvanceToWalkUpAmends] - passed in by the caller (rather than read
 *   from FeatureFlagStore here) so that a connected component re-renders when the flag toggles.
 * @returns {Object} derived split display values.
 */
export const deriveSplitDisplayValues = (trips, isReturnPart = false, enableAdvanceToWalkUpAmends = false) => {
    const items = Array.from(trips);

    const { outwardSingleItems, returnSingleItems, returnItems } = getGroupedTrips({ items });

    const isReturn = returnItems?.length > 0;
    const isSingleReturn = returnSingleItems?.length > 0;

    const outwardSingleSplitFares = getSortedFaresFromItems(outwardSingleItems);
    const returnSingleSplitFares = getSortedFaresFromItems(returnSingleItems);
    const returnSplitFares = getSortedFaresFromItems(returnItems);

    let isOutboundExpired = false;
    if (returnSplitFares?.length) {
        isOutboundExpired = returnSplitFares.some((fare) => fare?.outwardJourney?.isExpired());
    } else if (outwardSingleSplitFares?.length && returnSingleSplitFares?.length) {
        isOutboundExpired = outwardSingleSplitFares.some((fare) => fare?.outwardJourney?.isExpired());
    }

    let isReturnExpired = false;
    if (returnSplitFares?.length) {
        isReturnExpired = returnSplitFares.some((fare) => fare?.returnJourney?.isExpired());
    } else if (returnSingleSplitFares?.length) {
        isReturnExpired = returnSingleSplitFares.some((fare) => fare?.outwardJourney?.isExpired());
    }

    const isReturnToggled = isReturnPart !== null
        ? isReturnPart
        : ((isReturn || isSingleReturn) && isOutboundExpired);

    const splitFares = getSplitFares({ items, isReturn: isReturnToggled });

    const splitFaresLength = splitFares.length;
    const firstSplitFare = splitFares[0];
    const lastSplitFare = splitFares[splitFaresLength - 1];

    const adults = items?.[0]?.getTotalMaxAdults() ?? 0;
    const children = items?.[0]?.getTotalMaxChildren() ?? 0;

    const passengersCount = adults + children;

    const tickets = splitFares.flatMap((fare) => fare.tickets.toArray());

    const railcards = splitFares.flatMap((fare) => fare.getAllRailcards().toArray());

    let departureTime;
    if (isReturnToggled && lastSplitFare?.returnJourney) {
        departureTime = lastSplitFare.returnJourney.getDepartureTime();
    } else {
        departureTime = firstSplitFare?.outwardJourney?.getDepartureTime();
    }

    let arrivalTime;
    if (isReturnToggled && firstSplitFare?.returnJourney) {
        arrivalTime = firstSplitFare.returnJourney.getArrivalTime();
    } else {
        arrivalTime = lastSplitFare?.outwardJourney?.getArrivalTime();
    }

    let origin;
    if (isReturnToggled && lastSplitFare?.returnJourney) {
        origin = lastSplitFare.returnJourney.getOrigin() || get(firstSplitFare, 'destination');
    } else {
        origin = firstSplitFare?.outwardJourney?.getOrigin();
    }

    let destination;
    if (isReturnToggled && firstSplitFare?.returnJourney) {
        destination = firstSplitFare.returnJourney.getDestination() || get(lastSplitFare, 'origin');
    } else {
        destination = lastSplitFare?.outwardJourney?.getDestination();
    }

    let staticDestination;
    if (isReturnToggled && firstSplitFare?.returnJourney) {
        staticDestination = get(firstSplitFare.returnJourney.getOrigin(), 'name') || get(lastSplitFare, 'destination.name');
    } else {
        staticDestination = lastSplitFare?.outwardJourney?.getDestination()?.name;
    }

    let departureDateText;
    if (isReturnToggled && lastSplitFare?.returnJourney) {
        const dt = lastSplitFare.returnJourney.getDepartureTime();
        departureDateText = dt?.format('ddd, D MMM YYYY');
    } else if (firstSplitFare?.outwardJourney) {
        const dt = firstSplitFare.outwardJourney.getDepartureTime();
        departureDateText = dt?.format('ddd, D MMM YYYY');
    }

    const duration = (!arrivalTime || !departureTime) ? null : getDuration(arrivalTime, departureTime);

    const outboundLegs = returnSplitFares?.length
        ? returnSplitFares.flatMap((fare) => get(fare, 'outwardJourney.legs', []))
        : (outwardSingleSplitFares ? outwardSingleSplitFares.flatMap((fare) => get(fare, 'outwardJourney.legs', [])) : []);

    const inboundLegs = returnSplitFares?.length
        ? [...returnSplitFares].reverse().flatMap((fare) => get(fare, 'returnJourney.legs', []))
        : (returnSingleSplitFares ? returnSingleSplitFares.flatMap((fare) => get(fare, 'outwardJourney.legs', [])) : []);

    const legs = isReturnToggled ? inboundLegs : outboundLegs;

    const legsGroup = groupLegsBySplits(legs);

    const isRegularTicket = splitFares?.length === 1;

    const splitTicketTypes = isRegularTicket
        ? []
        : splitFares?.map((splitFare) => splitFare?.ticketType);

    const regularTicketType = isRegularTicket && splitFares ? splitFares[0].ticketType : null;

    const isPlusBus = splitFares.some((fare) => fare.isPlusBus());
    const isRoversRangers = splitFares.some((fare) => fare.ticketType.isRoversRangers());
    const isGroupSave = splitFares.some((fare) => fare.isGroupSave());
    const isFlexi = splitFares.every((fare) => fare.isFlexi());
    const isAnytime = splitFares.every((fare) => fare.isAnytime());
    const isAdvance = splitFares.some((fare) => fare.isAdvance());
    const isAmendableFareType = enableAdvanceToWalkUpAmends || isAdvance;

    const outwardJourney = splitFares[0]?.outwardJourney;
    const outwardJourneyValidFrom = outwardJourney?.validFrom;
    const outwardJourneyValidTo = (() => {
        const lastFareIndex = splitFares.length - 1;
        return splitFares[lastFareIndex]?.outwardJourney?.validTo;
    })();

    const returnJourney = splitFares[0]?.returnJourney;
    const returnJourneyValidFrom = returnJourney?.validFrom;
    const returnJourneyValidTo = (() => {
        const lastFareIndex = splitFares.length - 1;
        return splitFares[lastFareIndex]?.returnJourney?.validTo;
    })();

    const validFrom = isReturnPart ? returnJourneyValidFrom : outwardJourneyValidFrom;
    const validTo = isReturnPart ? returnJourneyValidTo : outwardJourneyValidTo;

    let ticketType = null;
    if (Array.isArray(splitTicketTypes) && splitTicketTypes.length) {
        if (isAdvance) {
            ticketType = SplitTicketTypes.ADVANCE;
        } else if (isFlexi || isAnytime) {
            ticketType = SplitTicketTypes.FLEXIBLE;
        } else {
            ticketType = SplitTicketTypes.LIMITED;
        }
    }

    const ticketTypeName = ticketType ? t(`ticket.split.type.${ticketType}`) : null;

    const splitFaresTotalPrice = splitFares.reduce((total, fare) => total + fare.getTotalPrice(), 0);

    return {
        splitFares,
        isReturnToggled,
        isSingleReturn,
        isReturn,
        origin,
        legsGroup,
        destination,
        staticDestination,
        duration,
        departureDateText,
        legs,
        outboundLegs,
        inboundLegs,
        tickets,
        railcards,
        adults,
        children,
        passengersCount,
        arrivalTime,
        departureTime,
        isRegularTicket,
        splitTicketTypes,
        isOutboundExpired,
        isReturnExpired,
        regularTicketType,
        outwardSingleItems,
        returnSingleItems,
        returnItems,
        outwardSingleSplitFares,
        returnSingleSplitFares,
        returnSplitFares,
        isPlusBus,
        isRoversRangers,
        isGroupSave,
        isFlexi,
        isAdvance,
        isAmendableFareType,
        ticketType,
        ticketTypeName,
        outwardJourneyValidFrom,
        outwardJourneyValidTo,
        returnJourneyValidFrom,
        returnJourneyValidTo,
        validFrom,
        validTo,
        splitFaresTotalPrice,
    };
};

/**
 * Whether a split group has a genuine return part to render. A trip can carry
 * outwardSplitReferenceId/returnSplitReferenceId pointing at a split group that
 * no longer resolves to anything (e.g. it was superseded by a later amend), in
 * which case deriveSplitDisplayValues finds no fares for the return part and
 * every display value would be blank. Guards against rendering that blank row.
 * @param {Array<OrderTrip>} items - trips belonging to one split group.
 * @returns {boolean} whether a return-part SplitPart should be rendered.
 */
export const hasReturnPartToRender = (items) => {
    if (!items.some((trip) => trip.isPartOfSplitAsRegularReturnSingle() || trip.isPartOfSplitAsRegularOutwardSingle())) {
        return false;
    }
    return deriveSplitDisplayValues(items, true).splitFares.length > 0;
};

/**
 * Whether a split group's trips all point unambiguously to ONE physical
 * direction via their own cross-reference field, with no trip present for the
 * other direction at all (e.g. that direction was fully amended away, leaving
 * only the untouched passenger's original trips behind). Split fares are
 * always stored under the outwardSingle fare slot regardless of physical
 * direction, so with no counterpart to compare against, getGroupedTrips falls
 * back to bucketing these trips as outward, and getSplitFares's isReturn=true
 * fallback (see getSplitFares above) treats those same items as the return
 * fares too — meaning callers unaware of this would render both a default and
 * a return-labeled part for the identical trips, one of them mislabeled.
 * @param {Array<OrderTrip>} items - trips belonging to one split group.
 * @returns {'outward'|'return'|null} the sole direction, or null when the
 *   group has genuine content on both sides (render both parts as normal).
 */
export const soleDirectionOfOrphanedGroup = (items) => {
    if (!items || !items.length) {
        return null;
    }
    const hasOutwardRef = (item) => !!item.outwardSplitReferenceId;
    const hasReturnRef = (item) => !!item.returnSplitReferenceId;

    if (items.every(hasOutwardRef) && !items.some(hasReturnRef)) {
        return 'return';
    }
    if (items.every(hasReturnRef) && !items.some(hasOutwardRef)) {
        return 'outward';
    }
    return null;
};
